import { ChangeEvent as ReactChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { PositionID, PositionNames } from '../../../common/Positions/Constants';
import { RemoteProcedureCall, useApplicationStateLoader } from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../../config';
import { ADMIN } from './IDs';
import styled from 'styled-components';

const formatGroupAdminIdCode = (groupAdminIdCode: string = '') => groupAdminIdCode.replace(/(.{5})(?=.)/g, '$1-');

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const COPY_SIGN_UP_URL_LABELS = {
  COPY_SIGN_UP_URL: 'Copy Sign-Up Link',
  SIGN_UP_URL_COPIED: 'Copied Sign-Up Link',
  ADMIN_GROUP_ID_CODE_COPIED: 'Copied',
};

const GroupAdminTools = styled.div`
  width: min(100%, 48em);
  margin: 0 auto;
`;
const ToolSection = styled.section`
  margin-bottom: 2em;
`;

export type AdminToolsProps = {
  authorization: string;
  groupAdminIdCode?: string;
  adminIsSystemAdmin: boolean;
  adminIsGroupAdmin: boolean;
};

export const AdminTools: FC<AdminToolsProps> = ({
  authorization,
  groupAdminIdCode,
  adminIsSystemAdmin,
  adminIsGroupAdmin,
}) => {
  const showDevTools = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.get('showDevTools') === 'true';
  }, []);
  const SYNC_USER_PROFILES_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/sync-user-profiles',
      args: [],
    }),
    [authorization]
  );
  const { loading: syncUserProfilesLoading, makeRemoteProcedureCall: syncUserProfiles } = useApplicationStateLoader({
    identifier: ADMIN.SYNC_USER_PROFILES,
    remoteProcedureCall: SYNC_USER_PROFILES_RPC,
    manual: true,
  });
  const [selectedPositionID, setSelectedPositionID] = useState<PositionID | undefined>(undefined);
  const onSelectedPositionIDChange = useCallback(({ target: { value } }: ReactChangeEvent<HTMLSelectElement>) => {
    if (value && value in PositionID) {
      setSelectedPositionID(value as PositionID);
    }
  }, []);
  const [signUpURLCopyLabel, setSignUpURLCopyLabel] = useState<string>(COPY_SIGN_UP_URL_LABELS.COPY_SIGN_UP_URL);
  const [adminGroupIdCodeCopyLabel, setAdminGroupIdCodeCopyLabel] = useState<string | undefined>(undefined);
  const onCopySignUpURL = useCallback(() => {
    if (selectedPositionID) {
      window.navigator.clipboard
        .writeText(
          `${window.location.origin}?position_id=${selectedPositionID}${
            groupAdminIdCode ? `&id_code=${groupAdminIdCode}` : ''
          }`
        )
        .then(() => {
          setSignUpURLCopyLabel(COPY_SIGN_UP_URL_LABELS.SIGN_UP_URL_COPIED);
          setTimeout(() => {
            setSignUpURLCopyLabel(COPY_SIGN_UP_URL_LABELS.COPY_SIGN_UP_URL);
          }, 2000);
        });
    }
  }, [selectedPositionID, groupAdminIdCode]);
  const onSyncUserProfiles = useCallback(() => {
    void syncUserProfiles();
  }, [syncUserProfiles]);
  const onCopyAdminGroupIdCode = useCallback(() => {
    if (groupAdminIdCode) {
      window.navigator.clipboard.writeText(formatGroupAdminIdCode(groupAdminIdCode)).then(() => {
        setAdminGroupIdCodeCopyLabel(COPY_SIGN_UP_URL_LABELS.ADMIN_GROUP_ID_CODE_COPIED);
        setTimeout(() => {
          setAdminGroupIdCodeCopyLabel(undefined);
        }, 2000);
      });
    }
  }, [groupAdminIdCode]);

  return (
    <div>
      {adminIsGroupAdmin ? (
        <GroupAdminTools>
          <ToolSection>
            <h4>Create Sign-Up Links</h4>
            <p>
              Choose the position, then copy and send the generated sign-up link to the users you want to add to your
              organization. The link includes your organization code, so anyone who signs up through it is automatically
              associated with your organization and the selected position.
            </p>
            <select value={selectedPositionID || ''} onChange={onSelectedPositionIDChange}>
              <option value="">Select Position</option>
              {Object.keys(PositionID).map((posID) => (
                <option key={posID} value={posID}>
                  {PositionNames[posID as PositionID]}
                </option>
              ))}
            </select>
            <button disabled={!selectedPositionID} onClick={onCopySignUpURL}>
              {signUpURLCopyLabel}
            </button>
          </ToolSection>
          <hr />
          <ToolSection>
            <h4>Your Organization Code</h4>
            <p>This code identifies your organization. Click the code to copy it and share it directly when needed.</p>
            <input
              readOnly
              onClick={onCopyAdminGroupIdCode}
              style={{
                backgroundColor: adminGroupIdCodeCopyLabel ? 'var(--pico-primary-background)' : undefined,
                transition: 'background-color 0.3s',
              }}
              value={adminGroupIdCodeCopyLabel ? adminGroupIdCodeCopyLabel : formatGroupAdminIdCode(groupAdminIdCode)}
            />
          </ToolSection>
        </GroupAdminTools>
      ) : (
        <>
          <h3>Tools</h3>
          <div>
            <h5>Create Sign-Up Link:</h5>
            <select value={selectedPositionID || ''} onChange={onSelectedPositionIDChange}>
              <option value="">Select Position</option>
              {Object.keys(PositionID).map((posID) => (
                <option key={posID} value={posID}>
                  {PositionNames[posID as PositionID]}
                </option>
              ))}
            </select>
            <button disabled={!selectedPositionID} onClick={onCopySignUpURL}>
              {signUpURLCopyLabel}
            </button>
          </div>
        </>
      )}
      {adminIsSystemAdmin && showDevTools ? (
        <>
          <br />
          <div>
            <h5>Synchronize User Account Info To Profiles:</h5>
            <view
              style={{
                color: 'white',
                backgroundColor: 'darkred',
                padding: '0.5em',
                borderRadius: '0.5em',
              }}
            >
              HINT: If you're not a developer, don't touch this stuff.
            </view>
            <br />
            <br />
            <button disabled={syncUserProfilesLoading} onClick={onSyncUserProfiles}>
              {syncUserProfilesLoading ? (
                <article
                  aria-busy="true"
                  style={{ display: 'inline-block', margin: '0 0.5em 0 0', padding: 0 }}
                ></article>
              ) : undefined}
              Sync Now
            </button>
          </div>
        </>
      ) : undefined}
    </div>
  );
};
